import { useEffect, useState, useRef, useCallback } from 'react';
import { Form, FormInstance } from 'antd';
import { Modal } from '@bancobarigui/barihaus';

import { useErrorHandler } from './useErrorHandler';
import { useAlertContext } from '@/contexts/alert.context';

export interface CRUDModalService<T, TCreate, TUpdate> {
  getAll: () => Promise<T[]>;
  create: (data: TCreate) => Promise<T>;
  update: (id: string, data: TUpdate) => Promise<T>;
  delete: (id: string) => Promise<void>;
}

export interface UseCRUDModalListOptions<T, TCreate, TUpdate> {
  service: CRUDModalService<T, TCreate, TUpdate>;
  entityName: string;
  entityNameFeminine?: boolean;
  messages?: {
    loadError?: string;
    createSuccess?: string;
    updateSuccess?: string;
    deleteSuccess?: string;
    deleteError?: string;
    saveError?: string;
    deleteConfirmTitle?: string;
    deleteConfirmContent?: string;
  };
  mapToFormValues?: (item: T) => Record<string, unknown>;
  mapFromFormValues?: (values: Record<string, unknown>, isEditing: boolean) => TCreate | TUpdate;
  fetchOnMount?: boolean;
  /** Se false, nao faz requisicoes (usado quando servico esta offline) */
  enabled?: boolean;
}

export interface UseCRUDModalListReturn<T> {
  items: T[];
  loading: boolean;
  isModalOpen: boolean;
  editingItem: T | null;
  submitting: boolean;
  form: FormInstance;
  fetchItems: () => Promise<void>;
  handleNew: () => void;
  handleEdit: (record: T) => void;
  handleDelete: (id: string) => void;
  handleModalCancel: () => void;
  handleModalSubmit: () => Promise<void>;
}

export function useCRUDModalList<
  T extends { id: string },
  TCreate = Partial<T>,
  TUpdate = Partial<T>
>({
  service,
  entityName,
  entityNameFeminine = false,
  messages,
  mapToFormValues,
  mapFromFormValues,
  fetchOnMount = true,
  enabled = true,
}: UseCRUDModalListOptions<T, TCreate, TUpdate>): UseCRUDModalListReturn<T> {
  const [items, setItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<T | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const hasFetched = useRef(false);

  const [form] = Form.useForm();
  const { handleError } = useErrorHandler();
  const { notify } = useAlertContext();

  const suffix = entityNameFeminine ? 'a' : 'o';
  const defaultMessages = {
    loadError: `Erro ao carregar ${entityName}`,
    createSuccess: `${entityName} criad${suffix} com sucesso!`,
    updateSuccess: `${entityName} atualizad${suffix} com sucesso!`,
    deleteSuccess: `${entityName} excluíd${suffix} com sucesso!`,
    deleteError: `Erro ao excluir ${entityName}`,
    saveError: `Erro ao salvar ${entityName}`,
    deleteConfirmTitle: 'Confirmar exclusão',
    deleteConfirmContent: `Tem certeza que deseja remover est${entityNameFeminine ? 'a' : 'e'} ${entityName.toLowerCase()}? Esta ação não pode ser desfeita.`,
    ...messages,
  };

  const fetchItems = useCallback(async () => {
    // Nao fazer requisicao se desabilitado (servico offline)
    if (!enabled) return;

    setLoading(true);
    try {
      const response = await service.getAll();
      setItems(response);
    } catch (error) {
      handleError(error, defaultMessages.loadError);
    } finally {
      setLoading(false);
    }
  }, [service, handleError, defaultMessages.loadError, enabled]);

  const handleNew = useCallback(() => {
    setEditingItem(null);
    form.resetFields();
    setIsModalOpen(true);
  }, [form]);

  const handleEdit = useCallback((record: T) => {
    setEditingItem(record);
    if (mapToFormValues) {
      form.setFieldsValue(mapToFormValues(record));
    } else {
      form.setFieldsValue(record);
    }
    setIsModalOpen(true);
  }, [form, mapToFormValues]);

  const handleDelete = useCallback((id: string) => {
    Modal.confirm({
      title: defaultMessages.deleteConfirmTitle,
      content: defaultMessages.deleteConfirmContent,
      okText: 'Remover',
      cancelText: 'Cancelar',
      icon: null,
      centered: true,
      okButtonProps: {
        style: {
          backgroundColor: '#0090ff',
          borderColor: '#0090ff',
          color: '#fff',
          boxShadow: 'none',
        },
      },
      cancelButtonProps: {
        style: {
          backgroundColor: 'transparent',
          borderColor: '#101010',
          color: '#101010',
          boxShadow: 'none',
        },
      },
      styles: {
        footer: {
          display: 'flex',
          justifyContent: 'center',
          gap: 12,
        },
      },
      onOk: async () => {
        try {
          await service.delete(id);
          notify({
            type: 'success',
            message: defaultMessages.deleteSuccess,
          });
          fetchItems();
        } catch (error) {
          handleError(error, defaultMessages.deleteError);
        }
      },
    });
  }, [service, notify, handleError, fetchItems, defaultMessages]);

  const handleModalCancel = useCallback(() => {
    setIsModalOpen(false);
    setEditingItem(null);
    form.resetFields();
  }, [form]);

  const handleModalSubmit = useCallback(async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);

      if (editingItem) {
        const updateData = mapFromFormValues
          ? mapFromFormValues(values, true) as TUpdate
          : values as TUpdate;
        await service.update(editingItem.id, updateData);
        notify({
          type: 'success',
          message: defaultMessages.updateSuccess,
        });
      } else {
        const createData = mapFromFormValues
          ? mapFromFormValues(values, false) as TCreate
          : values as TCreate;
        await service.create(createData);
        notify({
          type: 'success',
          message: defaultMessages.createSuccess,
        });
      }

      handleModalCancel();
      fetchItems();
    } catch (error) {
      handleError(error, defaultMessages.saveError);
    } finally {
      setSubmitting(false);
    }
  }, [form, editingItem, service, notify, handleError, fetchItems, handleModalCancel, mapFromFormValues, defaultMessages]);

  useEffect(() => {
    if (fetchOnMount && enabled && !hasFetched.current) {
      hasFetched.current = true;
      fetchItems();
    }
  }, [fetchOnMount, fetchItems, enabled]);

  return {
    items,
    loading,
    isModalOpen,
    editingItem,
    submitting,
    form,
    fetchItems,
    handleNew,
    handleEdit,
    handleDelete,
    handleModalCancel,
    handleModalSubmit,
  };
}
